import { DateTime } from 'luxon';

import { dateFilterOperators } from '../../../../../containers/base-filter-bar-container/reference-data-criteria-arguments';

/**
 * @typedef {object} AppliedFilter
 * @property {string} filterById
 * @property {string|Array<string|number>} criterionArgumentValue
 * @property {DateFilterOperator | string} [criterionOperatorValue]
 */

/**
 * @typedef {object} CollectionFilter
 * @property {string} column
 * @property {Array<number>} values
 */

/**
 * @typedef {object} FilterRequest
 * @property {object} filter
 * @property {Array<CollectionFilter>} [filter.collectionFilters]
 * @property {DateRange} [filter.orderDateRange]
 */

/**
 * @typedef {'neverModified'|'dateToday'|'dateYesterday'|'dateLastWeek'|'dateLast30Days'|'dateOnOrBefore'|'dateOnOrAfter'|'dateThisMonth'|'dateLastMonth'|'dateCustomRange'|'dateWithinThePastXDays'|'dateWithinTheNextXDays'|'dateXDaysAgoOrMore'|'dateXDaysAheadOrMore'} DateFilterOperator
 */

/**
 * @typedef {object} DateRangeObject
 * @property {string} startRangeDate
 * @property {string} endRangeDate
 */

/**
 * @typedef {object} DateRange
 * @property {string | null} startDateUtc
 * @property {string | null} endDateUtc
 */

/**
 * Calculate date range from operator and value
 * @param {DateFilterOperator} dateOperator - The date filter operator
 * @param {string|number|DateRangeObject} argumentValue - The argument value (date string, number of days, or range object)
 * @returns {DateRange | null} The date range in ISO format with UTC times
 */
export const calculateDateRangeFromOperator = (dateOperator, argumentValue) => {
  const formatToIso = (dt) => dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'");

  switch (dateOperator) {
    case dateFilterOperators.dateToday:
      return {
        startDateUtc: formatToIso(DateTime.utc().startOf('day')),
        endDateUtc: formatToIso(DateTime.utc().endOf('day')),
      };

    case dateFilterOperators.dateYesterday:
      return {
        startDateUtc: formatToIso(DateTime.utc().minus({ days: 1 }).startOf('day')),
        endDateUtc: formatToIso(DateTime.utc().minus({ days: 1 }).endOf('day')),
      };

    case dateFilterOperators.dateLastWeek:
      return {
        startDateUtc: formatToIso(DateTime.utc().minus({ days: 7 }).startOf('day')),
        endDateUtc: formatToIso(DateTime.utc().endOf('day')),
      };

    case dateFilterOperators.dateLast30Days:
      return {
        startDateUtc: formatToIso(DateTime.utc().minus({ days: 30 }).startOf('day')),
        endDateUtc: formatToIso(DateTime.utc().endOf('day')),
      };

    case dateFilterOperators.dateThisMonth:
      return {
        startDateUtc: formatToIso(DateTime.utc().startOf('month')),
        endDateUtc: formatToIso(DateTime.utc().endOf('month')),
      };

    case dateFilterOperators.dateLastMonth:
      return {
        startDateUtc: formatToIso(DateTime.utc().minus({ months: 1 }).startOf('month')),
        endDateUtc: formatToIso(DateTime.utc().minus({ months: 1 }).endOf('month')),
      };

    case dateFilterOperators.dateOnOrBefore:
      return {
        startDateUtc: null,
        endDateUtc: formatToIso(DateTime.fromISO(argumentValue, { zone: 'utc' }).endOf('day')),
      };

    case dateFilterOperators.dateOnOrAfter:
      return {
        startDateUtc: formatToIso(DateTime.fromISO(argumentValue, { zone: 'utc' }).startOf('day')),
        endDateUtc: null,
      };

    case dateFilterOperators.dateCustomRange:
      return {
        startDateUtc: formatToIso(DateTime.fromISO(argumentValue.startRangeDate, { zone: 'utc' }).startOf('day')),
        endDateUtc: formatToIso(DateTime.fromISO(argumentValue.endRangeDate, { zone: 'utc' }).endOf('day')),
      };

    case dateFilterOperators.dateWithinThePastXDays:
      return {
        startDateUtc: formatToIso(
          DateTime.utc()
            .minus({ days: Number(argumentValue) })
            .startOf('day'),
        ),
        endDateUtc: formatToIso(DateTime.utc().endOf('day')),
      };

    case dateFilterOperators.dateWithinTheNextXDays:
      return {
        startDateUtc: formatToIso(DateTime.utc().startOf('day')),
        endDateUtc: formatToIso(
          DateTime.utc()
            .plus({ days: Number(argumentValue) })
            .endOf('day'),
        ),
      };

    case dateFilterOperators.dateXDaysAgoOrMore:
      return {
        startDateUtc: null,
        endDateUtc: formatToIso(
          DateTime.utc()
            .minus({ days: Number(argumentValue) })
            .endOf('day'),
        ),
      };

    case dateFilterOperators.dateXDaysAheadOrMore:
      return {
        startDateUtc: formatToIso(
          DateTime.utc()
            .plus({ days: Number(argumentValue) })
            .startOf('day'),
        ),
        endDateUtc: null,
      };

    default:
      return null;
  }
};

/**
 * Transform applied filters to API request format
 * @param {Array<AppliedFilter>} appliedFilters - The filters from filter bar
 * @returns {FilterRequest | object} The transformed filter object for API request
 */
export const transformFiltersToApiRequest = (appliedFilters) => {
  if (!appliedFilters || appliedFilters.length === 0) {
    return {};
  }

  const filterIdToColumnMap = {
    status: 'Status',
    warehouse: 'Warehouse',
    supplier: 'Supplier',
  };

  const collectionFiltersByColumn = {};
  let orderDateRange = null;

  appliedFilters.forEach((filter) => {
    const { filterById, criterionOperatorValue, criterionArgumentValue } = filter;

    if (filterById === 'orderDate') {
      orderDateRange = calculateDateRangeFromOperator(criterionOperatorValue, criterionArgumentValue);
    } else {
      const values = Array.isArray(criterionArgumentValue) ? criterionArgumentValue : [criterionArgumentValue];
      const columnName = filterIdToColumnMap[filterById] || filterById;

      if (!collectionFiltersByColumn[columnName]) {
        collectionFiltersByColumn[columnName] = [];
      }
      collectionFiltersByColumn[columnName].push(...values);
    }
  });

  const collectionFilters = Object.entries(collectionFiltersByColumn).map(([column, values]) => ({
    column,
    values: values.map((v) => v.toString()),
  }));

  const result = {};

  if (!collectionFilters.length && !orderDateRange) {
    return result;
  }

  result.filter = {};
  if (collectionFilters.length > 0) {
    result.filter.collectionFilters = collectionFilters;
  }
  if (orderDateRange) {
    result.filter.orderDateRange = orderDateRange;
  }

  return result;
};
